import React, { useEffect, useState } from "react";
import { api, money, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Users, Package, Trophy, DollarSign, Gavel, Shield, Wallet, CheckCircle2, TrendingUp, Zap } from "lucide-react";

export default function AdminPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [auctions, setAuctions] = useState([]);
  const [payouts, setPayouts] = useState(null);
  const [payoutFilter, setPayoutFilter] = useState("pending");
  const [markPaidDialog, setMarkPaidDialog] = useState(null); // auction obj
  const [payoutRef, setPayoutRef] = useState("");
  const [tab, setTab] = useState("overview");
  const [credits, setCredits] = useState(null);
  const [creditFilter, setCreditFilter] = useState("pending");
  const [markCreditDialog, setMarkCreditDialog] = useState(null); // redemption obj
  const [creditRef, setCreditRef] = useState("");
  const [waitlist, setWaitlist] = useState(null);

  const load = async () => {
    try {
      const [s, u, a] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/auctions"),
      ]);
      setStats(s.data); setUsers(u.data); setAuctions(a.data);
    } catch (e) {
      toast.error("Failed to load admin data");
    }
  };
  useEffect(() => { load(); }, []);

  const loadPayouts = async (status = payoutFilter) => {
    try {
      const { data } = await api.get(`/admin/payouts?status=${status}`);
      setPayouts(data);
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  useEffect(() => {
    if (tab === "payouts") loadPayouts(payoutFilter);
  }, [tab, payoutFilter]);

  const loadCredits = async (status = creditFilter) => {
    try {
      const { data } = await api.get(`/credit/admin/list?status=${status}`);
      setCredits(data);
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  useEffect(() => {
    if (tab === "credits") loadCredits(creditFilter);
  }, [tab, creditFilter]);

  const markCreditPaid = async () => {
    if (!markCreditDialog) return;
    try {
      await api.post(`/credit/admin/${markCreditDialog.id}/mark_paid`, {
        payout_reference: creditRef || null,
      });
      toast.success("Credit redemption paid");
      setMarkCreditDialog(null);
      setCreditRef("");
      loadCredits();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const loadWaitlist = async () => {
    try {
      const { data } = await api.get("/waitlist/admin/list");
      setWaitlist(data);
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  useEffect(() => {
    if (tab === "waitlist") loadWaitlist();
  }, [tab]);

  const exportWaitlistCsv = () => {
    if (!waitlist?.items?.length) return;
    const rows = [["email", "zip_code", "source", "position", "joined_at"]];
    waitlist.items.forEach((w) => {
      rows.push([w.email, w.zip_code || "", w.source || "", w.position || "", w.joined_at || ""]);
    });
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const markPaid = async () => {
    if (!markPaidDialog) return;
    try {
      await api.post(`/admin/payouts/${markPaidDialog.id}/mark_paid`, {
        payout_reference: payoutRef || null,
      });
      toast.success("Payout marked as paid");
      setMarkPaidDialog(null);
      setPayoutRef("");
      loadPayouts();
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const revertPaid = async (auctionId) => {
    if (!window.confirm("Mark this payout as pending again?")) return;
    try {
      await api.post(`/admin/payouts/${auctionId}/mark_pending`, {});
      toast.success("Reverted to pending");
      loadPayouts();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const updateRole = async (id, role) => {
    try {
      await api.patch(`/admin/users/${id}/role`, { role });
      toast.success("Role updated");
      load();
    } catch (e) { toast.error("Failed to update role"); }
  };

  const deleteAuction = async (id) => {
    if (!window.confirm("Delete this auction? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/auctions/${id}`);
      toast.success("Auction deleted");
      load();
    } catch (e) { toast.error("Failed to delete"); }
  };

  if (!user || user.role !== "admin") {
    return <div className="max-w-7xl mx-auto p-12 text-zinc-500">Admin access required.</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-center gap-3 mb-10">
        <Shield className="w-6 h-6 text-orange-500" />
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-orange-500">Control Room</div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tighter">Admin Panel</h1>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-10">
          <MetricCard icon={<Users />} label="Users" value={stats.users} />
          <MetricCard icon={<Package />} label="Active" value={stats.active_auctions} />
          <MetricCard icon={<Trophy />} label="Sold" value={stats.sold_auctions} />
          <MetricCard icon={<Gavel />} label="Bids" value={stats.total_bids} />
          <MetricCard icon={<DollarSign />} label="Gross Volume" value={money(stats.revenue)} />
          <MetricCard icon={<TrendingUp />} label="Platform Take" value={money(stats.platform_take)} accent />
          <MetricCard icon={<Zap />} label="Boost Revenue" value={money(stats.boost_revenue)} />
          <MetricCard icon={<Wallet />} label="Owed to Sellers" value={money(stats.pending_seller_payouts)} />
        </div>
      )}

      <div className="flex gap-2 mb-6 border-b border-zinc-800">
        {["overview", "payouts", "credits", "waitlist", "users", "auctions"].map((t) => (
          <button
            key={t}
            data-testid={`admin-tab-${t}`}
            onClick={() => setTab(t)}
            className={`px-4 py-3 text-xs uppercase tracking-widest font-semibold border-b-2 -mb-px transition-colors ${
              tab === t ? "border-orange-500 text-orange-500" : "border-transparent text-zinc-500 hover:text-white"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "users" && (
        <div className="bg-zinc-950 border border-zinc-800">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableHead className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Email</TableHead>
                <TableHead className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Name</TableHead>
                <TableHead className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Provider</TableHead>
                <TableHead className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Role</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} className="border-zinc-900 hover:bg-zinc-900/50">
                  <TableCell className="font-mono text-xs">{u.email}</TableCell>
                  <TableCell>{u.name}</TableCell>
                  <TableCell className="text-xs uppercase tracking-widest text-zinc-500">{u.provider}</TableCell>
                  <TableCell>
                    <Select value={u.role} onValueChange={(v) => updateRole(u.id, v)}>
                      <SelectTrigger data-testid={`role-${u.id}`} className="rounded-none bg-black border-zinc-800 w-32 h-8 text-xs uppercase">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-none bg-zinc-950 border-zinc-800">
                        <SelectItem value="user">User</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {tab === "auctions" && (
        <div className="bg-zinc-950 border border-zinc-800">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableHead className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Title</TableHead>
                <TableHead className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Seller</TableHead>
                <TableHead className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Location</TableHead>
                <TableHead className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 text-right">Current Bid</TableHead>
                <TableHead className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 text-right">Bids</TableHead>
                <TableHead className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Status</TableHead>
                <TableHead className="text-[10px] tracking-[0.2em] uppercase text-zinc-500"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {auctions.map((a) => (
                <TableRow key={a.id} className="border-zinc-900 hover:bg-zinc-900/50">
                  <TableCell className="max-w-xs truncate">{a.title}</TableCell>
                  <TableCell className="text-zinc-400 text-sm">{a.seller_name}</TableCell>
                  <TableCell className="text-zinc-400 text-sm">{a.city}, {a.state}</TableCell>
                  <TableCell className="text-right font-display font-bold tabular-nums text-orange-500">{money(a.current_bid)}</TableCell>
                  <TableCell className="text-right tabular-nums">{a.bid_count}</TableCell>
                  <TableCell><StatusBadge status={a.status} /></TableCell>
                  <TableCell>
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid={`delete-auction-${a.id}`}
                      onClick={() => deleteAuction(a.id)}
                      className="rounded-none bg-transparent border-zinc-800 hover:border-red-500 hover:text-red-500 h-8 text-xs uppercase tracking-widest"
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {tab === "overview" && stats && (
        <div className="bg-zinc-950 border border-zinc-800 p-8">
          <div className="text-[10px] tracking-[0.3em] uppercase text-orange-500 mb-4">Marketplace Overview</div>
          <p className="text-zinc-400 leading-relaxed max-w-2xl">
            You have <b className="text-white">{stats.users}</b> registered users and <b className="text-white">{stats.total_auctions}</b> total auctions.
            Of those, <b className="text-orange-500">{stats.active_auctions}</b> are live and <b className="text-emerald-500">{stats.sold_auctions}</b> have been sold.
            Gross volume processed: <b className="text-white">{money(stats.revenue)}</b>. Your platform take (8% buyer&apos;s premium on every sale): <b className="text-orange-500 font-display text-xl">{money(stats.platform_take)}</b>.
            You currently owe sellers <b className="text-white">{money(stats.pending_seller_payouts)}</b> in pending payouts.
          </p>
        </div>
      )}

      {tab === "payouts" && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="text-[10px] tracking-[0.3em] uppercase text-orange-500">Seller Payouts</div>
            <Select value={payoutFilter} onValueChange={setPayoutFilter}>
              <SelectTrigger data-testid="payout-filter" className="w-44 rounded-none bg-zinc-950 border-zinc-800 text-xs uppercase tracking-widest">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-zinc-950 border-zinc-800">
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {payouts && (
            <>
              {/* Per-seller summary */}
              {payouts.by_seller && payouts.by_seller.length > 0 && payoutFilter === "pending" && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                  {payouts.by_seller
                    .filter((s) => s.pending > 0)
                    .sort((a, b) => b.pending - a.pending)
                    .map((s) => (
                      <div key={s.seller_id} data-testid={`payout-seller-${s.seller_id}`} className="bg-zinc-950 border border-zinc-800 p-4">
                        <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Seller</div>
                        <div className="font-display text-lg font-bold uppercase tracking-tight text-white truncate mt-1">{s.seller_name}</div>
                        <div className="text-xs text-zinc-500 truncate mt-0.5">{s.seller_email}</div>
                        <div className="mt-4 flex items-baseline justify-between">
                          <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Owed</span>
                          <span className="font-display text-2xl font-black text-orange-500 tabular-nums">{money(s.pending)}</span>
                        </div>
                        <div className="text-xs text-zinc-500 mt-1">{s.count} sale{s.count === 1 ? "" : "s"}</div>
                      </div>
                    ))}
                </div>
              )}

              {/* Payout table */}
              <div className="bg-zinc-950 border border-zinc-800">
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      <TableHead className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Auction</TableHead>
                      <TableHead className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Seller</TableHead>
                      <TableHead className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 text-right">Winning Bid</TableHead>
                      <TableHead className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 text-right">Your Take</TableHead>
                      <TableHead className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 text-right">Seller Owed</TableHead>
                      <TableHead className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Status</TableHead>
                      <TableHead className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 text-right"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payouts.items.length === 0 && (
                      <TableRow className="border-zinc-900 hover:bg-transparent">
                        <TableCell colSpan={7} className="text-center py-8 text-zinc-500 text-sm">
                          No {payoutFilter === "all" ? "" : payoutFilter} payouts yet.
                        </TableCell>
                      </TableRow>
                    )}
                    {payouts.items.map((p) => (
                      <TableRow key={p.id} data-testid={`payout-row-${p.id}`} className="border-zinc-900 hover:bg-zinc-900/50">
                        <TableCell className="max-w-xs truncate">{p.title}</TableCell>
                        <TableCell>
                          <div className="text-sm text-white truncate">{p.business_name || p.seller_name}</div>
                          <div className="text-xs text-zinc-500 truncate">{p.seller_email}</div>
                        </TableCell>
                        <TableCell className="text-right font-display font-bold tabular-nums text-white">{money(p.winning_bid)}</TableCell>
                        <TableCell className="text-right font-display font-bold tabular-nums text-orange-500">{money(p.platform_take)}</TableCell>
                        <TableCell className="text-right font-display font-bold tabular-nums text-emerald-500">{money(p.seller_payout_owed)}</TableCell>
                        <TableCell>
                          <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold tracking-widest uppercase border ${
                            p.payout_status === "paid"
                              ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/40"
                              : "bg-orange-500/10 text-orange-500 border-orange-500/40"
                          }`}>{p.payout_status}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          {p.payout_status === "pending" ? (
                            <Button
                              onClick={() => setMarkPaidDialog(p)}
                              data-testid={`mark-paid-${p.id}`}
                              size="sm"
                              className="rounded-none bg-emerald-600 hover:bg-emerald-500 border border-emerald-500 h-8 text-xs uppercase tracking-widest font-semibold"
                            >
                              <CheckCircle2 className="w-3 h-3 mr-1" /> Mark Paid
                            </Button>
                          ) : (
                            <Button
                              onClick={() => revertPaid(p.id)}
                              data-testid={`revert-paid-${p.id}`}
                              variant="ghost"
                              size="sm"
                              className="rounded-none h-8 text-xs uppercase tracking-widest text-zinc-500 hover:text-orange-500 hover:bg-zinc-900"
                            >
                              Revert
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </div>
      )}

      {tab === "credits" && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="text-[10px] tracking-[0.3em] uppercase text-orange-500">Promo Credit Redemptions</div>
            <Select value={creditFilter} onValueChange={setCreditFilter}>
              <SelectTrigger data-testid="credit-filter" className="w-44 rounded-none bg-zinc-950 border-zinc-800 text-xs uppercase tracking-widest">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-zinc-950 border-zinc-800">
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {credits && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                <div className="bg-zinc-950 border border-zinc-800 p-4">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Total Pending</div>
                  <div className="font-display text-2xl font-black text-orange-500 tabular-nums mt-2">{money(credits.total_pending || 0)}</div>
                </div>
                <div className="bg-zinc-950 border border-zinc-800 p-4">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Total Paid</div>
                  <div className="font-display text-2xl font-black text-emerald-500 tabular-nums mt-2">{money(credits.total_paid || 0)}</div>
                </div>
                <div className="bg-zinc-950 border border-zinc-800 p-4">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Requests Shown</div>
                  <div className="font-display text-2xl font-black text-white tabular-nums mt-2">{credits.items?.length || 0}</div>
                </div>
              </div>

              <div className="bg-zinc-950 border border-zinc-800">
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      <TableHead className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Seller</TableHead>
                      <TableHead className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Email</TableHead>
                      <TableHead className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 text-right">Amount</TableHead>
                      <TableHead className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Requested</TableHead>
                      <TableHead className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Status</TableHead>
                      <TableHead className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Reference</TableHead>
                      <TableHead className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 text-right"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(!credits.items || credits.items.length === 0) && (
                      <TableRow className="border-zinc-900 hover:bg-transparent">
                        <TableCell colSpan={7} className="text-center py-8 text-zinc-500 text-sm">
                          No {creditFilter === "all" ? "" : creditFilter} credit redemptions yet.
                        </TableCell>
                      </TableRow>
                    )}
                    {credits.items?.map((c) => (
                      <TableRow key={c.id} data-testid={`credit-row-${c.id}`} className="border-zinc-900 hover:bg-zinc-900/50">
                        <TableCell className="text-sm text-white">{c.business_name || "—"}</TableCell>
                        <TableCell className="font-mono text-xs text-zinc-400">{c.user_email}</TableCell>
                        <TableCell className="text-right font-display font-bold tabular-nums text-emerald-500">{money(c.amount)}</TableCell>
                        <TableCell className="text-xs text-zinc-500">
                          {c.requested_at ? new Date(c.requested_at).toLocaleDateString() : "—"}
                        </TableCell>
                        <TableCell>
                          <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold tracking-widest uppercase border ${
                            c.status === "paid"
                              ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/40"
                              : "bg-orange-500/10 text-orange-500 border-orange-500/40"
                          }`}>{c.status}</span>
                        </TableCell>
                        <TableCell className="text-xs text-zinc-500 font-mono truncate max-w-[160px]">{c.payout_reference || "—"}</TableCell>
                        <TableCell className="text-right">
                          {c.status === "pending" && (
                            <Button
                              onClick={() => setMarkCreditDialog(c)}
                              data-testid={`mark-credit-paid-${c.id}`}
                              size="sm"
                              className="rounded-none bg-emerald-600 hover:bg-emerald-500 border border-emerald-500 h-8 text-xs uppercase tracking-widest font-semibold"
                            >
                              <CheckCircle2 className="w-3 h-3 mr-1" /> Mark Paid
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </div>
      )}

      {tab === "waitlist" && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="text-[10px] tracking-[0.3em] uppercase text-orange-500">Pre-Launch Buyer Waitlist</div>
            <Button
              onClick={exportWaitlistCsv}
              disabled={!waitlist?.items?.length}
              data-testid="waitlist-export-csv"
              className="rounded-none bg-orange-600 hover:bg-orange-500 border border-orange-500 h-9 text-xs uppercase tracking-widest font-semibold disabled:opacity-40"
            >
              Export CSV
            </Button>
          </div>

          {waitlist && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                <div className="bg-zinc-950 border border-orange-500 p-4">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Total Signups</div>
                  <div data-testid="waitlist-total-count" className="font-display text-3xl font-black text-orange-500 tabular-nums mt-2">{waitlist.total || 0}</div>
                </div>
                <div className="bg-zinc-950 border border-zinc-800 p-4">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">This Week</div>
                  <div className="font-display text-3xl font-black text-white tabular-nums mt-2">
                    {waitlist.items?.filter((w) => {
                      if (!w.joined_at) return false;
                      const d = new Date(w.joined_at);
                      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
                      return d > weekAgo;
                    }).length || 0}
                  </div>
                </div>
                <div className="bg-zinc-950 border border-zinc-800 p-4">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Unique ZIPs</div>
                  <div className="font-display text-3xl font-black text-white tabular-nums mt-2">
                    {new Set(waitlist.items?.filter((w) => w.zip_code).map((w) => w.zip_code)).size || 0}
                  </div>
                </div>
              </div>

              <div className="bg-zinc-950 border border-zinc-800">
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      <TableHead className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 w-16">#</TableHead>
                      <TableHead className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Email</TableHead>
                      <TableHead className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">ZIP</TableHead>
                      <TableHead className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Source</TableHead>
                      <TableHead className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Joined</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(!waitlist.items || waitlist.items.length === 0) && (
                      <TableRow className="border-zinc-900 hover:bg-transparent">
                        <TableCell colSpan={5} className="text-center py-8 text-zinc-500 text-sm">
                          No signups yet. Share your site link to start filling the waitlist.
                        </TableCell>
                      </TableRow>
                    )}
                    {waitlist.items?.map((w) => (
                      <TableRow key={w.id} data-testid={`waitlist-row-${w.id}`} className="border-zinc-900 hover:bg-zinc-900/50">
                        <TableCell className="font-display text-orange-500 tabular-nums">#{w.position}</TableCell>
                        <TableCell className="font-mono text-xs text-white">{w.email}</TableCell>
                        <TableCell className="font-mono text-xs text-zinc-400">{w.zip_code || "—"}</TableCell>
                        <TableCell className="text-xs uppercase tracking-widest text-zinc-500">{w.source || "—"}</TableCell>
                        <TableCell className="text-xs text-zinc-500">
                          {w.joined_at ? new Date(w.joined_at).toLocaleDateString() : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </div>
      )}

      <Dialog open={!!markPaidDialog} onOpenChange={(o) => !o && setMarkPaidDialog(null)}>
        <DialogContent className="rounded-none bg-zinc-950 border-zinc-800 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight">Mark Payout as Sent</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Confirm you sent the seller their share for this auction.
            </DialogDescription>
          </DialogHeader>
          {markPaidDialog && (
            <div className="space-y-4">
              <div className="text-sm text-zinc-400">
                Confirm you sent <b className="text-emerald-500 font-display text-lg">{money(markPaidDialog.seller_payout_owed)}</b> to <b className="text-white">{markPaidDialog.business_name || markPaidDialog.seller_name}</b> for auction <b className="text-white">{markPaidDialog.title}</b>.
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 block mb-2">Reference (optional)</label>
                <Input
                  data-testid="payout-reference-input"
                  placeholder="e.g., Stripe payout id, ACH ref, check #"
                  value={payoutRef}
                  onChange={(e) => setPayoutRef(e.target.value)}
                  className="rounded-none bg-black border-zinc-800 focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-0 industrial-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setMarkPaidDialog(null)}
              className="rounded-none bg-transparent border-zinc-700 hover:bg-zinc-900 text-xs uppercase tracking-widest"
            >
              Cancel
            </Button>
            <Button
              onClick={markPaid}
              data-testid="confirm-mark-paid"
              className="rounded-none bg-emerald-600 hover:bg-emerald-500 border border-emerald-500 text-xs uppercase tracking-widest font-semibold"
            >
              Confirm Payment Sent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!markCreditDialog} onOpenChange={(o) => !o && setMarkCreditDialog(null)}>
        <DialogContent className="rounded-none bg-zinc-950 border-zinc-800 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight">Pay Credit Redemption</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Confirm you sent this seller their promo credit payout.
            </DialogDescription>
          </DialogHeader>
          {markCreditDialog && (
            <div className="space-y-4">
              <div className="text-sm text-zinc-400">
                Confirm you sent <b className="text-emerald-500 font-display text-lg">{money(markCreditDialog.amount)}</b> to <b className="text-white">{markCreditDialog.business_name || markCreditDialog.user_email}</b>.
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 block mb-2">Reference (optional)</label>
                <Input
                  data-testid="credit-reference-input"
                  placeholder="e.g., Bluevine ACH ref, Zelle #, check #"
                  value={creditRef}
                  onChange={(e) => setCreditRef(e.target.value)}
                  className="rounded-none bg-black border-zinc-800 focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-0 industrial-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setMarkCreditDialog(null)}
              className="rounded-none bg-transparent border-zinc-700 hover:bg-zinc-900 text-xs uppercase tracking-widest"
            >
              Cancel
            </Button>
            <Button
              onClick={markCreditPaid}
              data-testid="confirm-mark-credit-paid"
              className="rounded-none bg-emerald-600 hover:bg-emerald-500 border border-emerald-500 text-xs uppercase tracking-widest font-semibold"
            >
              Confirm Payment Sent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MetricCard({ icon, label, value, accent }) {
  return (
    <div className={`bg-zinc-950 border p-4 ${accent ? "border-orange-500" : "border-zinc-800"}`}>
      <div className={accent ? "text-orange-500" : "text-zinc-500"}>{icon}</div>
      <div className={`font-display text-2xl font-black mt-2 tabular-nums ${accent ? "text-orange-500" : "text-white"}`}>{value}</div>
      <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mt-1">{label}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    active: "bg-orange-500/10 text-orange-500 border-orange-500/40",
    sold: "bg-emerald-500/10 text-emerald-500 border-emerald-500/40",
    ended: "bg-zinc-500/10 text-zinc-500 border-zinc-700",
  };
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold tracking-widest uppercase border ${map[status] || map.ended}`}>
      {status}
    </span>
  );
}
